import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { defaultAboutLoanForm } from "@/constants";
import { useCountrySettings } from "@/hooks/use-app-country";
import { Application, ApplicationPurpose } from "@/types/form";

export interface AboutLoanForm extends Application {
	isAffirmedRefinanceLoan: boolean;
}

export const useAboutForm = () => {
	const t = useTranslations("forms.errors.aboutLoanForm");
	const { loanAmount } = useCountrySettings();
	const { min, max } = loanAmount;

	const ApplicationSchema: z.ZodSchema<AboutLoanForm> = z
		.object({
			appliedAmount: z.number().min(min).max(max),
			isAffirmedRefinanceLoan: z.boolean(),
			appliedRepaymentTime: z
				.number()
				.int({ message: t("mustBeInteger") })
				.positive(),
			purpose: z.nativeEnum(ApplicationPurpose, {
				message: t("missingPurpose"),
			}),
			refinanceAmount: z.number(),
		})
		.superRefine((data, ctx) => {
			if (data.isAffirmedRefinanceLoan && !data.refinanceAmount) {
				ctx.addIssue({
					path: ["refinanceAmount"],
					message: t("missingRefinance"),
					code: z.ZodIssueCode.custom,
				});
			}

			if (
				data.isAffirmedRefinanceLoan &&
				data.refinanceAmount > data.appliedAmount
			) {
				ctx.addIssue({
					path: ["refinanceAmount"],
					message: t("refinanceToHigh"),
					code: z.ZodIssueCode.custom,
				});
			}
		});

	return {
		useForm: useForm<AboutLoanForm>({
			resolver: zodResolver(
				ApplicationSchema as unknown as z.ZodType<
					AboutLoanForm,
					AboutLoanForm
				>
			),
			defaultValues: {
				...defaultAboutLoanForm,
				appliedAmount: loanAmount.default,
			},
			mode: "onTouched",
		}),
		schema: ApplicationSchema,
	};
};
